// NEW SYSTEM: Organization Subscription Status
export enum OrganizationSubscriptionStatus {
  TRIAL = 'TRIAL',
  /**
   * A card-free trial that ran out. The organization stays readable but every write is
   * refused server-side until a payment method is added. Distinct from PAST_DUE, which
   * means a card exists and was declined.
   */
  TRIAL_EXPIRED = 'TRIAL_EXPIRED',
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  CANCELED = 'CANCELED',
  PAUSED = 'PAUSED',
}

// NEW SYSTEM: Billing Period (renamed from BillingCycle)
export enum BillingPeriod {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

// LEGACY: Keep for backwards compatibility
export enum SubscriptionStatus {
  TRIAL = 'trial',
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  PAUSED = 'paused',
  EXPIRED = 'expired',
}

export enum SubscriptionBillingCycle {
  DAILY = 'daily',
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
}

export enum SubscriptionPlan {
  STANDARD = 'standard',
}

export enum PaymentMethodType {
  CREDIT_CARD = 'credit_card',
}

export enum TransactionStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  PENDING = 'pending',
  REFUNDED = 'refunded',
}

export enum TransactionType {
  CHARGE = 'charge',
  REFUND = 'refund',
  RETRY = 'retry',
}

export enum PricingType {
  PLAN = 'plan',
  PER_USER = 'per_user',
  ADDON = 'addon',
}

// NEW SYSTEM: Subscription Type Enum
export enum SubscriptionType {
  BASE = 'BASE',
  ADDON = 'ADDON',
}

// NEW SYSTEM: Plan Snapshot Interface (grandfathered pricing)
export interface PlanSnapshot {
  planId: string;
  name: string;
  basePriceCents: number;
  includedSeats: number;
  isAddOn: boolean;
  purchasedAt: string;
  seatCountingRoles?: string[];
}

// NEW SYSTEM: Organization Subscription Interface
export interface OrganizationSubscription {
  id: string;
  organizationId: string;
  planTemplateId: string;
  subscriptionType: SubscriptionType;
  status: OrganizationSubscriptionStatus;
  billingPeriod: BillingPeriod;
  trialEndsAt?: string;
  startedAt: string;
  canceledAt?: string;
  nmiSubscriptionId?: string;
  includedSeats: number;
  planSnapshot?: PlanSnapshot;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  planName?: string;
  organizationName?: string;
}

// LEGACY: Keep for backwards compatibility
export interface Subscription {
  subscriptionId: string;
  organizationId: string;
  organizationName?: string; // For Super Admin view
  nmiSubscriptionId?: string;
  planName: SubscriptionPlan;
  status: SubscriptionStatus;
  billingCycle: SubscriptionBillingCycle;
  amount: number;
  startDate: string;
  trialEndDate?: string;
  nextBillingDate?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  canceledAt?: string;
  cancellationReason?: string;
  failedPaymentAttempts: number;
  lastPaymentAttempt?: string;
  lastPaymentError?: string;
  createdByEmail?: string; // For audit trail
  updatedByEmail?: string; // For audit trail
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethod {
  paymentMethodId: string;
  organizationId: string;
  nmiCustomerVaultId?: string;
  type: PaymentMethodType;
  last4?: string;
  brand?: string;
  expiryMonth?: string;
  expiryYear?: string;
  accountHolderName?: string;
  accountType?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionTransaction {
  transactionId: string;
  subscriptionId: string;
  nmiTransactionId?: string;
  amount: number;
  status: TransactionStatus;
  type: TransactionType;
  nmiResponseCode?: string;
  failureReason?: string;
  nmiResponseText?: string;
  transactionDate: string;
  createdAt: string;
}

export interface SubscriptionAnalytics {
  totalActive: number;
  totalTrial: number;
  totalCanceled: number;
  totalPastDue: number;
  monthlyRecurringRevenue: number;
  annualRecurringRevenue: number;
  trialConversionRate: number;
  newSubscriptionsThisMonth: number;
  canceledThisMonth: number;
  churnRate: number;
}

export interface SubscriptionsList {
  subscriptions: Subscription[];
  count: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateSubscriptionInput {
  organizationId: string;
  billingCycle: SubscriptionBillingCycle;
  paymentMethodType: PaymentMethodType;
  firstName: string;
  lastName: string;
  email: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  company?: string;
  // Payment Token (from Collect.js)
  paymentToken?: string;
  // Google Pay
  googlePaymentData?: string;
  // Credit Card (if not using token)
  ccNumber?: string;
  ccExp?: string;
  cvv?: string;
  // ACH
  checkName?: string;
  checkAba?: string;
  checkAccount?: string;
  accountHolderType?: string;
  accountType?: string;
  secCode?: string;
}

export interface SubscriptionFilters {
  page?: number;
  limit?: number;
  status?: SubscriptionStatus;
  search?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface AddPaymentMethodInput {
  organizationId: string;
  paymentMethodType: PaymentMethodType;
  firstName: string;
  lastName: string;
  email: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  company?: string;
  // Payment Token (from Collect.js)
  paymentToken?: string;
  // Google Pay
  googlePaymentData?: string;
  // Credit Card (if not using token)
  ccNumber?: string;
  ccExp?: string;
  cvv?: string;
  // ACH
  checkName?: string;
  checkAba?: string;
  checkAccount?: string;
  accountHolderType?: string;
  accountType?: string;
  secCode?: string;
}

export interface SubscriptionPlanTemplate {
  id: string;
  name: string;
  payMethod?: string;
  basePriceCents: number;
  includedSeats: number;
  isPublic: boolean;
  isAddOn: boolean;
  seatCountingRoles?: string[];
  trialDurationDays?: number;
  /**
   * When true a card is captured before the organization is created. When false the plan
   * runs a card-free trial and billing is collected at trial expiry. Independent of
   * trialDurationDays; the backend rejects the combination of no trial and no upfront
   * payment, since that leaves no billing gate at all.
   */
  requiresPaymentUpfront?: boolean;
  validFrom?: string;
  validTo?: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Sprint 17, story-082. Additive. Seat add-ons linked to this plan (public pricing/onboarding
   * consumer only) — see `PlanAddonSummary`'s own doc comment for the org-scoped counterpart.
   */
  addOns?: PlanAddonSummary[];
}

/**
 * Sprint 17, story-082. Shared shape for both `SubscriptionPlanTemplate.addOns` (public plan
 * pricing) and `getLinkedAddOnsForOrganization` (the org's own current entitlements) —
 * deliberately identical so the UI shares one rendering path regardless of which query supplied
 * the data.
 */
export interface PlanAddonSummary {
  id: string;
  name: string;
  includedSeats: number;
  priceCents: number;
}

export interface ActiveSeat {
  userId: string;
  userEmail: string;
  userName: string;
  roleName: string;
  createdAt: Date;
}

// LEGACY - to be removed
export interface PricingConfig {
  pricingConfigId: string;
  pricingType: PricingType;
  key: string;
  name: string;
  description?: string;
  amount: number;
  billingCycle?: SubscriptionBillingCycle;
  trialDurationDays?: number;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}
